import { useMemo, useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog.tsx";

import { Body } from "@sprout/typography.tsx";
import { useTranslation } from "react-i18next";
import DreamJobIcon from "@/sprout/icons/dream_job";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@/sprout/text_input";
import type Application from "@/types/application";
import ApplicationCardList from "../applications/application_card_list";
import { useDebounce } from "react-use";
import { IconButton } from "@/sprout/icon_button";
import CloseIcon from "@/sprout/icons/close";
import { markAsDreamJob } from "@/api/dream_job";
import "@/stylesheets/dream_job_icon.scss";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import { getDate } from "date-fns";
import { daysUntilNextMonth } from "@/utils/date_utils";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

type ApplicationsPage = {
  active?: {
    applications: Application[];
    total_pages: number;
  };
};

type Props = {
  label?: string;
};

const DreamJobPickerModal = ({ label }: Props = {}) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "dream_job_modal" });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selectedApplication, setSelectedApplication] = useState<Application | undefined>(
    undefined,
  );

  const updateSearch = (val: string) => {
    setSearch(val);
    setSelectedApplication(undefined);
  };

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    200,
    [search],
  );

  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ["dream_job_modal", "applications", debouncedSearch],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const params = new URLSearchParams({
        page: pageParam.toString(),
        active_only: "true",
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const response = await fetch(`/applications.json?${params}`);
      return response.json();
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApplicationsPage, allPages: ApplicationsPage[]) => {
      const section = lastPage.active;
      if (section && section.total_pages > allPages.length) {
        return allPages.length + 1;
      }
      return undefined;
    },
    staleTime: 30 * 1000,
  });

  const allApplications = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap(
        (page: ApplicationsPage) => page.active?.applications ?? [],
      ),
    [query.data],
  );

  const applications: Application[] = useMemo(
    () => allApplications.filter((app: Application) => !app.dream_job && app.dream_job_eligible),
    [allApplications],
  );

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: ["dream_job_modal"] });
    queryClient.invalidateQueries({ queryKey: ["applications"] });
  };

  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSearch("");
      setDebouncedSearch("");
      setSelectedApplication(undefined);
    }
  };

  const onApplicationSelect = (id: number) => {
    setSelectedApplication(applications.find((application) => application.id === id));
  };

  const makeDreamJob = async () => {
    if (!selectedApplication) {
      return;
    }

    markAsDreamJob({
      dreamJobPath: selectedApplication.dreamJobPath,
      invalidateQuery,
    });
  };

  return (
    <Dialog onOpenChange={handleDialogClose}>
      <DialogTrigger>
        <SecondaryButton size="medium">{label ?? t("trigger")}</SecondaryButton>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] h-[90dvh] sm:h-[70vh] max-h-[90dvh] bg-white flex flex-col p-0 overflow-hidden">
        <DialogHeader className="w-full pt-6 pr-6 pl-6 shrink-0">
          <DialogTitle>
            <div className="text-xl">{t("title")}</div>
          </DialogTitle>
          <DialogClose asChild>
            <IconButton icon={CloseIcon} size="md" label={t("cancel")} />
          </DialogClose>
        </DialogHeader>
        <div className="flex flex-col gap-5 shrink-0 px-6">
          <Body>
            {t("description")}
            <span className="ml-2">
              <a
                href="https://support.greenhouse.io/hc/en-us/articles/43418495049499-MyGreenhouse-FAQ-for-Candidates#h_01KAA2KNJNJT5E6WFAX1YQFBKE"
                target="_blank"
                rel="noopener noreferrer"
                className="text-link underline"
              >
                {t("learn_more")}
              </a>
            </span>
          </Body>
          <div className="flex p-4 w-full border rounded-lg bg-marigold-25 border-marigold-e50 text-dark-green">
            <div className="bg-marigold-e50 self-center dream-job-icon">
              <DreamJobIcon size="md" />
            </div>
            <p className="mx-2 self-center text-base">
              {getDate(new Date()) <= 15
                ? t("remaining_tokens", { count: 1 })
                : t("days_left_to_use", { count: daysUntilNextMonth() })}
            </p>
          </div>
          <TextInput
            className="self-center"
            id="dream-job-search"
            placeholder={t("placeholder")}
            onChange={(event) => updateSearch(event.currentTarget.value || "")}
            maxLength={255}
            value={search}
            narrow
            icon={<SearchIcon />}
            onClear={() => updateSearch("")}
          />
        </div>
        <div className="overflow-auto min-h-0 flex-1 px-6 py-4">
          <ApplicationCardList
            selectedApplicationId={selectedApplication?.id}
            applications={applications}
            onApplicationSelect={onApplicationSelect}
            showCallout={false}
            narrow
            addLinks
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => query.fetchNextPage()}
          />
        </div>
        <div className="border-b border-[var(--gray-700)] w-full shrink-0"></div>
        <DialogFooter className="pb-6 pl-6 pr-6 shrink-0">
          <DialogClose>
            <SecondaryButton>
              <div>{t("cancel")}</div>
            </SecondaryButton>
          </DialogClose>
          <DialogClose>
            <PrimaryButton onClick={makeDreamJob} disabled={!selectedApplication}>
              <div>{t("save")}</div>
            </PrimaryButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DreamJobPickerModal;
